/**
 * Context exposing the organization's configured proficiency levels.
 *
 * Components rendering level icons (ProficiencyStatusIcon and friends) consume
 * this context so changes made in /administration/competences (Niveaux tab) are
 * reflected everywhere in the app — not only in that admin tab.
 */
import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from "react";
import {
  fetchProficiencyLevelLabels,
  type ProficiencyLevel,
} from "@/services/organizationService";
import { useCurrentOrganization } from "./OrganizationContext";

type LegacyLevelKey = "achieved" | "almostAchieved" | "evolution" | "notMet";

interface ProficiencyLevelsContextType {
  /** Configured levels (may be empty while loading or if org has no override). */
  levels: ProficiencyLevel[];
  /** Resolve a legacy status key to the matching configured level, if any. */
  getLevelByKey: (key: LegacyLevelKey) => ProficiencyLevel | undefined;
  /** Re-fetch from DB (called after admin save). */
  refresh: () => Promise<void>;
  loading: boolean;
}

const ProficiencyLevelsContext = createContext<ProficiencyLevelsContextType>({
  levels: [],
  getLevelByKey: () => undefined,
  refresh: async () => {},
  loading: true,
});

export function useProficiencyLevels() {
  return useContext(ProficiencyLevelsContext);
}

export function ProficiencyLevelsProvider({ children }: { children: ReactNode }) {
  const { orgId } = useCurrentOrganization();
  const [levels, setLevels] = useState<ProficiencyLevel[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!orgId) {
      setLevels([]);
      setLoading(false);
      return;
    }
    try {
      const stored = await fetchProficiencyLevelLabels(orgId);
      setLevels(Array.isArray(stored.levels) ? stored.levels : []);
    } catch {
      setLevels([]);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  const getLevelByKey = useCallback(
    (key: LegacyLevelKey) => {
      // Match either by stable id (preferred) or by icon key (legacy).
      return levels.find((lv) => lv.id === key) ?? levels.find((lv) => lv.icon === key);
    },
    [levels],
  );

  return (
    <ProficiencyLevelsContext.Provider value={{ levels, getLevelByKey, refresh: load, loading }}>
      {children}
    </ProficiencyLevelsContext.Provider>
  );
}